import { useCallback, useEffect, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { useNavigate } from "react-router-dom";
import { authAtom } from "../../../lib/auth-store.js";
import { apiCall } from "../../../lib/api-client.js";
import { useApiQuery } from "../../../lib/useApiQuery.js";
import {
  llmConfigPath,
  rolesPath,
  roleProvisioningPath,
  roleProvisioningStartPath,
  roleProvisioningByIdPath,
} from "../../../lib/api-paths.js";
import {
  ApiError,
  type LlmProviderConfig,
  type PublicRole,
  type RoleProvisioningListResponse,
  type RoleProvisioningTask,
  type RoleSuggestion,
  type StartRpResponse,
} from "../../../lib/api-types.js";
import { ErrorBanner, InfoBanner } from "../../../components/ErrorBanner.js";
import { InlineLoading, Spinner } from "../../../components/Spinner.js";
import {
  PrimaryButton,
  SecondaryButton,
  TextInput,
  TextArea,
} from "../../../components/FormField.js";
import { cn } from "../../../lib/cn.js";
import type { OnboardingStepProps } from "../types.js";

const POLL_INTERVAL_MS = 4_000;
const POLL_TIMEOUT_MS = 90_000;

/** 可编辑的角色草案（从 LLM suggestion 派生，管理员可改名/改描述/排除）。 */
interface EditableRole extends RoleSuggestion {
  included: boolean;
}

type Phase =
  | "loading"
  | "idle"
  | "analyzing"
  | "editing"
  | "applied"
  | "llm-missing";

/**
 * Step 3 — 角色推荐 + wizard 内可编辑（缺口2）。
 * 流程：LLM 前置 gate → 生成推荐 → 轮询 → 可编辑卡片（名/描述/取舍）→ 确认应用。
 */
export function Step3Roles({
  org,
  onReadyChange,
}: OnboardingStepProps): JSX.Element {
  const auth = useAtomValue(authAtom);
  const navigate = useNavigate();

  const { data: llmConfig, loading: llmLoading } =
    useApiQuery<LlmProviderConfig>(llmConfigPath(org.code), [org.code]);
  const {
    data: roles,
    loading: rolesLoading,
    refetch: refetchRoles,
  } = useApiQuery<PublicRole[]>(rolesPath(org.code), [org.code]);

  const [phase, setPhase] = useState<Phase>("loading");
  const [task, setTask] = useState<RoleProvisioningTask | null>(null);
  const [editable, setEditable] = useState<EditableRole[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollTimer = useRef<number | null>(null);

  const llmReady = Boolean(llmConfig && llmConfig.hasKey);
  const hasRoles = (roles?.length ?? 0) > 0;

  // 初始相位：已有角色 → applied（就绪）；否则看 LLM 是否就绪
  useEffect(() => {
    if (llmLoading || rolesLoading) return;
    setPhase((prev) => {
      if (prev === "editing" || prev === "analyzing") return prev;
      if (hasRoles) return "applied";
      return llmReady ? "idle" : "llm-missing";
    });
  }, [llmLoading, rolesLoading, hasRoles, llmReady]);

  // 就绪 = 已应用出角色
  useEffect(() => {
    onReadyChange(phase === "applied" && hasRoles);
  }, [phase, hasRoles, onReadyChange]);

  const seedFromTask = useCallback((t: RoleProvisioningTask) => {
    setTask(t);
    setEditable(
      (t.suggestion?.roles ?? []).map((r) => ({ ...r, included: true })),
    );
    setPhase("editing");
  }, []);

  // 挂载时：若已有 awaiting-confirm 任务（中途离开续做）→ 直接进编辑
  useEffect(() => {
    if (!auth || hasRoles) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiCall<RoleProvisioningListResponse>(
          "GET",
          `${roleProvisioningPath(org.code)}?status=awaiting-confirm&page=1&pageSize=1`,
          { token: auth.token },
        );
        const latest = res.rows[0];
        if (!cancelled && latest?.suggestion) seedFromTask(latest);
      } catch {
        /* 无则忽略 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth, org.code, hasRoles, seedFromTask]);

  const stopPolling = useCallback(() => {
    if (pollTimer.current != null) {
      window.clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const handleGenerate = useCallback(async () => {
    if (!auth) return;
    setBusy(true);
    setError(null);
    try {
      await apiCall<StartRpResponse>("POST", roleProvisioningStartPath(org.code), {
        token: auth.token,
        body: { type: "initial-bootstrap" },
      });
      setPhase("analyzing");
      const startedAt = Date.now();
      stopPolling();
      pollTimer.current = window.setInterval(() => {
        void (async () => {
          try {
            const res = await apiCall<RoleProvisioningListResponse>(
              "GET",
              `${roleProvisioningPath(org.code)}?page=1&pageSize=1`,
              { token: auth.token },
            );
            const latest = res.rows[0];
            if (latest?.status === "awaiting-confirm" && latest.suggestion) {
              stopPolling();
              seedFromTask(latest);
            } else if (latest?.status === "rejected") {
              stopPolling();
              setError(
                latest.error === "llm-not-configured"
                  ? "未配置 LLM，请先到「LLM 配置」填写后重试。"
                  : `分析失败：${latest.error ?? "未知错误"}`,
              );
              setPhase(llmReady ? "idle" : "llm-missing");
            } else if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
              stopPolling();
              setError("分析超时，请稍后重试。");
              setPhase("idle");
            }
          } catch {
            /* 轮询瞬断忽略，下次再试 */
          }
        })();
      }, POLL_INTERVAL_MS);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "触发失败");
      setPhase("idle");
    } finally {
      setBusy(false);
    }
  }, [auth, org.code, stopPolling, seedFromTask, llmReady]);

  const handleApply = useCallback(async () => {
    if (!auth || !task?.suggestion) return;
    const included = editable.filter((r) => r.included);
    if (included.length === 0) {
      setError("请至少保留一个角色。");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiCall("POST", roleProvisioningByIdPath(org.code, task.id, "/apply"), {
        token: auth.token,
        body: {
          adjustedSuggestion: {
            ...task.suggestion,
            roles: included.map(({ included: _omit, ...r }) => r),
          },
        },
      });
      await refetchRoles();
      setPhase("applied");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "应用失败");
    } finally {
      setBusy(false);
    }
  }, [auth, task, editable, org.code, refetchRoles]);

  const updateRole = useCallback(
    (code: string, patch: Partial<EditableRole>) => {
      setEditable((prev) =>
        prev.map((r) => (r.code === code ? { ...r, ...patch } : r)),
      );
    },
    [],
  );

  if (phase === "loading" || llmLoading || rolesLoading) {
    return <InlineLoading label="加载角色状态…" />;
  }

  return (
    <div className="space-y-4">
      {error ? (
        <ErrorBanner onDismiss={() => setError(null)}>{error}</ErrorBanner>
      ) : null}

      {phase === "llm-missing" ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
          <p className="text-sm font-medium text-dosia-text">
            还差一步：配置一个 AI 模型
          </p>
          <p className="mt-1 text-sm text-dosia-text-secondary">
            角色推荐由 AI 完成。请先填写一个模型的密钥（Claude 或 OpenAI
            兼容），回来即可一键生成。
          </p>
          <PrimaryButton
            className="mt-3"
            onClick={() => navigate("../llm-config")}
          >
            去配置 LLM
          </PrimaryButton>
        </div>
      ) : null}

      {phase === "idle" ? (
        <div className="rounded-lg border border-dosia-border bg-dosia-bg-light p-6 text-center">
          <p className="text-sm text-dosia-text-secondary">
            点下方按钮，AI 会读取你们的部门结构，生成一套推荐角色。
          </p>
          <PrimaryButton
            className="mt-4 px-5"
            onClick={() => void handleGenerate()}
            disabled={busy}
          >
            {busy ? "正在提交…" : "生成推荐角色"}
          </PrimaryButton>
        </div>
      ) : null}

      {phase === "analyzing" ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dosia-border bg-dosia-bg-light py-12">
          <Spinner size="lg" />
          <p className="text-sm text-dosia-text-secondary">
            AI 正在分析组织架构，约需 30 秒…
          </p>
        </div>
      ) : null}

      {phase === "editing" ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-dosia-text-secondary">
              AI 推荐了 {editable.length} 个角色。你可以改名、改描述，或取消勾选不需要的，然后确认应用。
            </p>
          </div>
          <ul className="space-y-3">
            {editable.map((role) => (
              <RoleEditCard
                key={role.code}
                role={role}
                onChange={(patch) => updateRole(role.code, patch)}
              />
            ))}
          </ul>
          {task?.suggestion?.llmProvider ? (
            <p className="text-xs text-dosia-text-dim">
              由 {task.suggestion.llmProvider} · {task.suggestion.llmModel} 生成
            </p>
          ) : null}
          <div className="flex items-center gap-2 pt-1">
            <PrimaryButton onClick={() => void handleApply()} disabled={busy}>
              {busy ? "应用中…" : "确认应用这套角色"}
            </PrimaryButton>
            <SecondaryButton
              onClick={() => void handleGenerate()}
              disabled={busy}
            >
              重新生成
            </SecondaryButton>
          </div>
        </div>
      ) : null}

      {phase === "applied" ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-lg border border-emerald-400/30 bg-emerald-400/5 px-3 py-2 text-sm text-dosia-text">
            <span className="text-emerald-700">✓</span>
            已为团队生成 {roles?.length ?? 0} 个角色。
          </div>
          <ul className="space-y-2">
            {(roles ?? []).map((r) => (
              <li
                key={r.id}
                className="rounded-lg border border-dosia-border bg-dosia-bg-light px-3 py-2"
              >
                <span className="text-sm font-medium text-dosia-text">
                  {r.name}
                </span>
                <span className="ml-2 font-mono text-xs text-dosia-text-dim">
                  {r.code}
                </span>
                {r.description ? (
                  <p className="mt-0.5 text-xs text-dosia-text-secondary">
                    {r.description}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
          <SecondaryButton
            onClick={() => void handleGenerate()}
            disabled={busy}
          >
            重新生成一套
          </SecondaryButton>
        </div>
      ) : null}
    </div>
  );
}

function RoleEditCard({
  role,
  onChange,
}: {
  role: EditableRole;
  onChange: (patch: Partial<EditableRole>) => void;
}): JSX.Element {
  return (
    <li
      className={cn(
        "rounded-lg border p-3 transition-colors duration-150",
        role.included
          ? "border-dosia-border bg-dosia-bg-light"
          : "border-dosia-border-light bg-dosia-bg opacity-60",
      )}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={role.included}
          onChange={(e) => onChange({ included: e.target.checked })}
          className="mt-2"
          aria-label={`保留角色 ${role.name}`}
        />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <TextInput
              value={role.name}
              disabled={!role.included}
              onChange={(e) => onChange({ name: e.target.value })}
              className="max-w-[200px]"
            />
            <span className="font-mono text-xs text-dosia-text-dim">
              {role.code}
            </span>
            <span className="ml-auto flex items-center gap-2 text-xs text-dosia-text-secondary">
              {typeof role.confidence === "number" ? (
                <span>置信度 {Math.round(role.confidence * 100)}%</span>
              ) : null}
              <span>{role.assignedUserIds.length} 人</span>
            </span>
          </div>
          <TextArea
            value={role.description ?? ""}
            disabled={!role.included}
            rows={2}
            placeholder="这个角色负责什么？（可留空）"
            onChange={(e) => onChange({ description: e.target.value })}
            className="font-sans"
          />
          {role.reason ? (
            <p className="text-xs italic text-dosia-text-dim">{role.reason}</p>
          ) : null}
        </div>
      </div>
    </li>
  );
}
